<template>
    <div id="modal-orders-sales-orders-apply-credit">
        <b-modal :id="modalName" title="Apply Credits" scrollable hide-footer size="xl" @shown="loadAvailableCredits">
            <template v-slot:modal-title>
                Apply Credits
            </template>

            <div v-if="loading" class="text-center">
                <b-spinner></b-spinner>
                <p>Loading available credits...</p>
            </div>

            <div v-else>
                <!-- Credits Table -->
                <b-table-simple hover small responsive bordered>
                    <b-thead>
                        <b-tr>
                            <b-th>ID</b-th>
                            <b-th>Date</b-th>
                            <b-th>Reference</b-th>
                            <b-th>Notes</b-th>
                            <b-th class="text-right">Amount Total</b-th>
                            <b-th class="text-right">Amount Available</b-th>
                            <b-th>Applied To</b-th>
                            <b-th>Created By</b-th>
                        </b-tr>
                    </b-thead>
                    <b-tbody>
                        <b-tr v-for="credit in availableCredits" :key="credit.id">
                            <b-td>{{ credit.id }}</b-td>
                            <b-td>{{ credit.date }}</b-td>
                            <b-td>{{ credit.reference || '-' }}</b-td>
                            <b-td>{{ credit.notes || '-' }}</b-td>
                            <b-td class="text-right">${{ numberWithCommas(credit.amount_total) }}</b-td>
                            <b-td class="text-right">${{ numberWithCommas(credit.amount_available) }}</b-td>
                            <b-td>
                                <div v-if="credit.credits_applieds && credit.credits_applieds.length > 0">
                                    <div v-for="applied in credit.credits_applieds" :key="applied.id">
                                        {{ applied.sales_order.sales_order_number }} - ${{ applied.applied_amount }}
                                    </div>
                                </div>
                                <span v-else>-</span>
                            </b-td>
                            <b-td>{{ credit.created_by_user ? credit.created_by_user.name : '-' }}</b-td>
                        </b-tr>
                        <b-tr v-if="availableCredits.length === 0">
                            <b-td colspan="8" class="text-center">No available credits found</b-td>
                        </b-tr>
                    </b-tbody>
                </b-table-simple>

                <!-- Summary Row -->
                <b-row class="mt-3">
                    <b-col cols="6">
                        <strong>Total Available: ${{ numberWithCommas(totalAvailable) }}</strong>
                    </b-col>
                    <b-col cols="6" class="text-right">
                        <strong>Balance Due: ${{ numberWithCommas(orderTotal) }}</strong>
                    </b-col>
                </b-row>

                <!-- Apply Credit Form -->
                <b-form @submit.stop.prevent="applyCredit" class="mt-3">
                    <b-form-row>
                        <b-col cols="6">
                            <b-form-group id="input-group-credit" label="Amount to Apply:" label-for="input-credit" label-size="sm">
                                <b-form-input 
                                    id="input-credit" 
                                    v-model="creditAmount" 
                                    type="text" 
                                    placeholder="0.00" 
                                    size="sm"
                                    autocomplete="off" 
                                    ref="setcreditinput" 
                                    @focus.native="$event.target.select()"
                                    @input="validateCreditAmount">
                                </b-form-input>
                            </b-form-group>
                        </b-col>
                        <b-col cols="6" class="d-flex align-items-end">
                            <div>
                                <span v-if="creditAmount > totalAvailable" class="text-danger">
                                    <strong>Amount exceeds available credits!</strong>
                                </span>
                                <span v-else-if="creditAmount > orderTotal" class="text-warning">
                                    <strong>Amount exceeds balance due!</strong>
                                </span>
                                <span v-else class="text-success">
                                    <strong>Valid amount</strong>
                                </span>
                            </div>
                        </b-col>
                    </b-form-row>

                    <b-form-row class="button-row">
                        <b-col>
                            <b-button 
                                type="submit" 
                                variant="primary" 
                                class="btn-sm"
                                :disabled="!isValidAmount || submitting">
                                <span v-if="submitting">
                                    <b-spinner small type="grow"></b-spinner>
                                    Applying...
                                </span>
                                <span v-else>Apply</span>
                            </b-button>
                            <b-button 
                                type="button" 
                                variant="secondary" 
                                class="btn-sm ml-2"
                                @click="closeModal">
                                Close
                            </b-button>
                        </b-col>
                    </b-form-row>
                </b-form>
            </div>
        </b-modal>
    </div>
</template>

<script>
import commonMixin from '../../../../../mixins/common'

export default {
    mixins: [commonMixin],
    
    props: [
        'available',
        'customerId',
        'orderTotal',
        'requireConfirmation'
    ],

    components: {

    },

    data() {
        return {
            modalName: 'modal-orders-sales-orders-apply-credit',
            creditAmount: 0,
            submitting: false,
            loading: true,
            availableCredits: [],
            totalAvailable: 0
        }
    },

    methods: {
        loadAvailableCredits() {
            this.loading = true;
            let url = `api/orders/creditmemos/getavailablecredits/${this.customerId}`;
            
            axios.get(url)
                .then((res) => {
                    this.availableCredits = res.data.credits;
                    this.totalAvailable = res.data.totalAvailable;
                    this.loading = false;
                    
                    // Set initial credit amount to the minimum of available credits and balance due
                    this.creditAmount = Math.min(this.totalAvailable, this.orderTotal);
                })
                .catch(() => {
                    this.has_error = true;
                    this.loading = false;
                });
        },

        validateCreditAmount() {
            // Remove any non-numeric characters except decimal point
            this.creditAmount = this.creditAmount.toString().replace(/[^0-9.]/g, '');
            
            // Ensure only one decimal point
            const parts = this.creditAmount.split('.');
            if (parts.length > 2) {
                this.creditAmount = parts[0] + '.' + parts.slice(1).join('');
            }
            
            // Limit to 2 decimal places
            if (parts.length === 2 && parts[1].length > 2) {
                this.creditAmount = parts[0] + '.' + parts[1].substring(0, 2);
            }
        },

        applyCredit() {
            if (!this.isValidAmount) {
                return;
            }

            this.submitting = true;
            this.$emit('setCredit', parseFloat(this.creditAmount));
            this.closeModal(this.modalName);
        },

        closeModal() {
            if (this.creditAmount > 0 && this.requireConfirmation) {
                this.$bvModal.msgBoxConfirm('Are you sure you want to close without applying the credit?', {
                    title: 'Close Without Applying',
                    size: 'sm',
                    buttonSize: 'sm',
                    okVariant: 'danger',
                    okTitle: 'Yes, Close',
                    cancelTitle: 'No, Stay',
                    footerClass: 'p-2',
                    hideHeaderClose: false,
                    centered: true
                })
                .then(value => {
                    if (value) {
                        this.$bvModal.hide(this.modalName);
                    }
                })
                .catch(err => {
                    // An error occurred
                });
            } else {
                this.$bvModal.hide(this.modalName);
            }
        },

        focusInput() {
            this.$refs.setcreditinput.focus();
        }
    },

    computed: {
        isValidAmount() {
            const amount = parseFloat(this.creditAmount);
            return amount > 0 && amount <= this.totalAvailable && amount <= this.orderTotal;
        }
    },

    mounted() {

    }
}
</script>
<style lang="scss">
#modal-orders-sales-orders-apply-credit {
    .modal-dialog {
        max-width: 90% !important;
    }

    .modal-content {
        .button-row {
            margin-top: 15px;
        }

        .text-danger {
            color: red;
        }

        .text-warning {
            color: orange;
        }

        .text-success {
            color: green;
        }
    }
}
</style>
